<script>
  import { createEventDispatcher, onDestroy, tick } from 'svelte';
  import { X } from '../lib/icons/index.js';
  import { returnFocus, trapTab } from '../lib/modal.js';

  /**
   * A panel that slides in from the right edge, full height, over a dimmed
   * page: the shape of the «Nuova fattura» composer, for a form that needs room
   * but not a page of its own.
   *
   * It is a native <dialog> opened with showModal(), so the browser does the
   * hard parts of a modal: the page behind is inert and out of the
   * accessibility tree, Tab stays inside, the sheet sits in the top layer above
   * every z-index, and Escape raises `cancel`. What is left to this component:
   *
   *   - Escape and a click on the dimmed page ask the owner to close (the
   *     `close` event) instead of closing on their own, so the owner's `open`
   *     stays the single source of truth;
   *   - the first field gets focus on opening, and focus goes back to what
   *     opened the sheet when it closes;
   *   - Tab wraps from the last control to the first, as in every other
   *     dialog here, instead of leaving for the browser's own toolbar.
   *
   * The owner renders the body in the default slot and the actions in the
   * `footer` slot; a submit button there reaches a <form> in the body through
   * its `form` attribute.
   */

  /** Whether the sheet is shown. The owner sets it; `close` asks it to clear it. */
  export let open = false;
  export let title = '';
  /** A short label above the title: what kind of thing the sheet is about. */
  export let eyebrow = '';
  export let closeLabel = 'Chiudi';
  /** Prefix for the ids the heading and the description get. */
  export let id = 'side-sheet';
  /** A selector, inside the sheet, of what should get focus when it opens. */
  export let initialFocus = '';

  const dispatch = createEventDispatcher();

  let dialog;
  let trigger = null;
  let pressedOutside = false;

  $: if (dialog) sync(open);

  async function sync(shouldBeOpen) {
    if (shouldBeOpen && !dialog.open) {
      trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
      await tick();
      // The body stays in the DOM while the sheet is closed, so it would
      // reopen scrolled to wherever the previous record was left.
      const body = dialog.querySelector('.side-sheet-body');
      if (body) body.scrollTop = 0;
      focusFirst();
    } else if (!shouldBeOpen && dialog.open) {
      dialog.close();
    }
  }

  function focusFirst() {
    const target = (initialFocus && dialog.querySelector(initialFocus))
      || dialog.querySelector(
        '.side-sheet-body :is(input:not([type="hidden"]), select, textarea, button, [tabindex]:not([tabindex="-1"])):not([disabled])',
      );
    (target || dialog).focus({ preventScroll: true });
  }

  function requestClose() {
    dispatch('close');
  }

  // Escape. Not closed here: the owner may want to keep the sheet open.
  function onCancel(event) {
    event.preventDefault();
    requestClose();
  }

  // The dialog has closed — because `open` went false, or because the browser
  // closed it anyway (a second Escape in a row is not cancellable). Either way
  // the owner hears about it, and focus goes home.
  function onClosed() {
    if (open) requestClose();
    returnFocus(trigger);
    trigger = null;
  }

  // A click on the dimmed page lands on the <dialog> itself, outside its box.
  // Only a press that also started out there counts: dragging a text selection
  // out of a field must not throw the form away.
  function outside(event) {
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right
      || event.clientY < rect.top || event.clientY > rect.bottom;
  }
  function onPointerDown(event) {
    pressedOutside = event.target === dialog && outside(event);
  }
  function onClick(event) {
    if (pressedOutside && event.target === dialog && outside(event)) requestClose();
    pressedOutside = false;
  }

  onDestroy(() => {
    if (dialog?.open) dialog.close();
  });
</script>

<!-- The click handler only closes on the backdrop; the keyboard path is
     Escape (`cancel`) and the close button. -->
<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
<dialog
  bind:this={dialog}
  class="side-sheet"
  aria-labelledby="{id}-title"
  on:cancel={onCancel}
  on:close={onClosed}
  on:keydown={(event) => trapTab(event, dialog)}
  on:pointerdown={onPointerDown}
  on:click={onClick}
>
  <div class="side-sheet-layout">
    <header class="side-sheet-header">
      <div class="side-sheet-heading">
        {#if eyebrow}<span class="side-sheet-eyebrow">{eyebrow}</span>{/if}
        <h2 id="{id}-title" class="side-sheet-title">{title}</h2>
      </div>
      <button type="button" class="icon-button" aria-label={closeLabel} title={closeLabel} on:click={requestClose}>
        <X size={20} aria-hidden="true" />
      </button>
    </header>
    <div class="side-sheet-body">
      <slot />
    </div>
    {#if $$slots.footer}
      <footer class="side-sheet-footer">
        <slot name="footer" />
      </footer>
    {/if}
  </div>
</dialog>
